import React, { useState, useEffect } from "react";
import { Loader2, Database, Shield, Zap } from "lucide-react";
import logoSrc from "/logo.png";

interface LoadingScreenProps {
  message?: string;
}

export default function LoadingScreen({ message = "Loading Verity..." }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    { icon: Shield, text: "Initializing security protocols...", progress: 20 },
    { icon: Database, text: "Loading data connections...", progress: 40 },
    { icon: Zap, text: "Optimizing performance...", progress: 60 },
    { text: "Finalizing setup...", progress: 80 },
    { text: "Ready to launch!", progress: 100 }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const stepInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= steps.length - 1) {
          clearInterval(stepInterval);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(stepInterval);
  }, []);

  const currentStepData = steps[currentStep] || steps[steps.length - 1];
  const IconComponent = currentStepData.icon;

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center z-50">
      <div className="text-center max-w-md mx-auto px-6">
        {/* Logo/Icon */}
        <div className="mb-8">
          <div className="w-24 h-24 mx-auto flex items-center justify-center">
            <img src={logoSrc} alt="Juggernaut Logo" className="w-24 h-24 object-contain animate-pulse" />
          </div>
        </div>

        {/* Loading Animation */}
        <div className="mb-8">
          <div className="relative">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
            {IconComponent && (
              <div className="absolute inset-0 flex items-center justify-center">
                <IconComponent className="w-6 h-6 text-white animate-pulse" />
              </div>
            )}
          </div>
        </div>

        {/* Loading Message */}
        <div className="space-y-3 mb-8">
          <h2 className="text-2xl font-semibold text-white">{message}</h2>
          <p className="text-sm text-white/70">{currentStepData.text}</p>
        </div>

        {/* Progress Bar */}
        <div className="w-full bg-white/10 rounded-full h-2 overflow-hidden mb-6">
          <div 
            className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Progress Percentage */}
        <div className="text-sm text-white/60 mb-6">
          {Math.round(progress)}%
        </div>

        {/* Loading Dots */}
        <div className="flex justify-center space-x-2">
          {[0, 1, 2].map((i) => (
            <div 
              key={i}
              className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"
              style={{ animationDelay: `${i * 0.1}s` }}
            ></div>
          ))}
        </div>

        {/* Version Info */}
        <div className="mt-8 text-xs text-white/40">
        © 2026 Verity Audit Solutions. All Rights Reserved.
        </div>
      </div>
    </div>
  );
}
